import React, { useMemo, useState, useEffect, useRef } from 'react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Generic edit cell component with autocomplete suggestions
// filterFields: Array of field names to filter by (e.g., ['dmv_make'])
// field: The field to extract suggestions from (automatically provided by DataGrid)
const AutocompleteEditCell = ({
  id,
  value,
  field,
  api,
  rows,
  filterFields = []
}) => {
  // Track input value separately from selected value to prevent auto-selection
  const [inputValue, setInputValue] = useState(value || '');
  // Ref to prevent duplicate commits
  const isCommittingRef = useRef(false);

  // Initialize inputValue when value prop changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Get the current row to access filter field values
  const currentRow = api.getRow(id);

  // Get unique values for the target field from rows matching all filterFields
  const suggestions = useMemo(() => {
    if (!currentRow || !rows || !filterFields.length) {
      return [];
    }

    // Filter rows where all filterFields match the current row's values
    const matchingRows = rows.filter((row) => {
      return filterFields.every((filterField) => {
        const currentValue = currentRow[filterField];
        const rowValue = row[filterField];
        // Match if both values exist and are equal (case-insensitive for strings)
        if (currentValue == null || rowValue == null) {
          return false;
        }
        return String(currentValue).trim() === String(rowValue).trim();
      });
    });

    // Extract unique, non-empty values from the target field
    const uniqueValues = matchingRows
      .map((row) => row[field])
      .filter((val) => val != null && String(val).trim() !== '')
      .map((val) => String(val).trim());

    // Remove duplicates and sort alphabetically
    return [...new Set(uniqueValues)].sort();
  }, [currentRow, rows, filterFields, field]);

  const commitChange = () => {
    // Prevent duplicate commits
    if (isCommittingRef.current) {
      return;
    }
    isCommittingRef.current = true;
    api.commitCellChange({ id, field });
    api.setCellMode(id, field, 'view');
    // Reset the flag after a short delay to allow for future edits
    setTimeout(() => {
      isCommittingRef.current = false;
    }, 100);
  };

  const handleChange = (event, newValue) => {
    // Prevent handling if we're already committing (e.g., after Enter key)
    if (isCommittingRef.current) {
      return;
    }
    // newValue can be a string (when freeSolo is true) or null
    const newFieldValue =
      typeof newValue === 'string' ? newValue : newValue || '';
    api.setEditCellValue({ id, field, value: newFieldValue });

    // Update inputValue when a value is selected from dropdown
    if (newValue && suggestions.includes(newValue)) {
      setInputValue(newFieldValue);
      // Small delay to ensure the value is set before committing
      setTimeout(() => {
        commitChange();
      }, 0);
    }
  };

  const handleInputChange = (event, newInputValue) => {
    // Update the input value state as user types
    setInputValue(newInputValue || '');
    // Update the cell value immediately as user types
    api.setEditCellValue({ id, field, value: newInputValue || '' });
  };

  const handleClose = () => {
    // Commit when dropdown closes (e.g., clicking outside)
    commitChange();
  };

  const handleInputBlur = () => {
    // Commit when input loses focus (e.g., clicking outside)
    commitChange();
  };

  const handleKeyDown = (event) => {
    // Commit on Enter key - use inputValue to prevent auto-selection
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      // Ensure the value is set before committing
      const valueToCommit = inputValue || '';
      api.setEditCellValue({ id, field, value: valueToCommit });
      // Use setTimeout to ensure the value is set before committing
      setTimeout(() => {
        commitChange();
      }, 0);
    }
    // Commit on Escape key
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      commitChange();
    }
  };

  return (
    <Autocomplete
      freeSolo
      options={suggestions}
      value={value || ''}
      inputValue={inputValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      onClose={handleClose}
      selectOnFocus={false}
      open
      autoFocus
      fullWidth
      style={{ height: '100%' }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          autoFocus={true}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          style={{ height: '100%' }}
          InputProps={{
            ...params.InputProps,
            disableUnderline: false,
            style: { height: '100%' }
          }}
          inputProps={{
            ...params.inputProps,
            style: { height: '100%', padding: 0 }
          }}
        />
      )}
      ListboxProps={{
        style: {
          maxHeight: '200px'
        }
      }}
    />
  );
};

export default AutocompleteEditCell;
